import { PermissionEnum } from '@Application/Security/Roles';
import DocumentDetailsPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails';
import DocumentListPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentList';
import NewInterviewConsentPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/CreateInterviewConsentPage';
import NewNotePage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/CreateNotePage';
import InterviewRecordingPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/RecordInterviewPage';
import InterviewDetailsPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails';
import InterviewConsentDetailsPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/Pages/InterviewConsentDetails';
import InterviewUploadPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload';
import ChooseWorkflowPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/ChooseWorkflow';
import PreparationPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation';
import RequirePermission from '@UI/Routing/RequirePermission';
import * as React from 'react';
import { Navigate, Outlet, Route } from 'react-router-dom';

/** Wraps a read-only screen: receives the action wording and the screen, returns the guarded screen. */
export type ReadGuard = (action: string, element: React.ReactElement) => React.ReactElement;

/**
 * Renders the deposit sub-routes (interviews, preparations, documents, local interviews) of a
 * dossier. A plain function, not a component: `<Routes>` accepts only `<Route>` elements and
 * fragments, so a wrapping component would never be matched. Write guards are fixed here; `readGuard`
 * dresses the read screens.
 */
const renderSubjectDepositRoutes = (readGuard: ReadGuard): React.ReactElement => {
	return (
		<React.Fragment>
			<Route path="interviews">
				{/*
					The three steps of creating an interview share one guard, held here rather than
					repeated on each: they are one act, and whoever may start it may finish it. `new` is a
					literal sibling of `:interview` below — React Router ranks a literal above a param
					whatever the source order, so the two can never be confused.
				*/}
				<Route
					path="new"
					element={
						<RequirePermission
							permission={PermissionEnum.INTERVIEW_WRITE}
							action="créer un entretien"
						>
							<Outlet />
						</RequirePermission>
					}
				>
					{/*
						No `index` route here, deliberately: a capture is only reached from a preparation,
						which opens the screen of its kind (`?preparation=`), and the kind is decided by
						which of these two literal routes was reached, never typed anywhere.
					*/}
					<Route path="interview" element={<NewInterviewConsentPage />} />
					<Route path="notes" element={<NewNotePage />} />
				</Route>
				<Route path=":interview">
					<Route index element={readGuard('consulter un entretien', <InterviewDetailsPage />)} />
					<Route
						path="consent"
						element={readGuard('consulter un consentement', <InterviewConsentDetailsPage />)}
					/>
					<Route
						path="upload"
						element={readGuard("suivre l'envoi d'un entretien", <InterviewUploadPage />)}
					/>
				</Route>
			</Route>
			<Route
				path="preparations/new"
				element={
					<RequirePermission
						permission={[
							PermissionEnum.INTERVIEW_WRITE,
							PermissionEnum.DOCUMENT_GENERATION_LAUNCH,
						]}
						mode="any"
						action="ouvrir une préparation"
					>
						<ChooseWorkflowPage />
					</RequirePermission>
				}
			/>
			<Route
				path="preparations/:preparation"
				element={readGuard('consulter une préparation', <PreparationPage />)}
			/>
			<Route path="documents">
				<Route index element={readGuard('consulter les documents', <DocumentListPage />)} />
				<Route
					path=":document"
					element={readGuard('consulter un document', <DocumentDetailsPage />)}
				/>
			</Route>
			{/*
				An interview captured on this device, before anything of it exists server-side. Reading it
				takes the dossier read; resuming its capture takes the write — two guards, not one.
			*/}
			<Route path="local-interviews/:localInterview">
				<Route index element={<Navigate to="upload" replace />} />
				<Route
					path="upload"
					element={readGuard("suivre l'envoi d'un entretien", <InterviewUploadPage />)}
				/>
				<Route
					path="recording"
					element={
						<RequirePermission
							permission={PermissionEnum.INTERVIEW_WRITE}
							action="créer un entretien"
						>
							<InterviewRecordingPage />
						</RequirePermission>
					}
				/>
			</Route>
		</React.Fragment>
	);
};

export default renderSubjectDepositRoutes;
